import { useContext } from 'react';

import { useOnboardingEligible } from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingEligible';
import { SPAViewContext } from '@confluence/spa-view-context';
import {
	useGetOnboardingState,
	deserializeState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState';
import { ONBOARDING_EXPERIENCES } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants';
import { useUserByLicensedProducts } from '@confluence/onboarding-helpers/entry-points/hooks/useUserByLicensedProducts';
import { fg } from '@confluence/feature-gating';

import { HOME_ONBOARDING_TAKEOVER_V2_ONBOARDING_STATE_KEYS } from '../homeOnboardingTakeoverV2OnboardingStateKeys';

/**
 * Custom hook to determine the eligibility for the Home Onboarding Takeover V2.
 *
 * This hook checks multiple conditions to determine if a user is eligible for the onboarding experience.
 * The conditions include:
 * 1. Either:
 *   a. The user has already been exposed to this takeover OR,
 *   b. They are an new user/not an established user.
 * 2. Whether the user is a site admin and a founding admin.
 * 3. Whether the user is a land user.
 *
 * @returns {boolean} - Returns `true` if the user passes all eligibility checks, otherwise `false`.
 */
export const useHomeOnboardingTakeoverV2Eligibility = () => {
	// Start with some preliminary data fetching
	const { isSiteAdmin } = useContext(SPAViewContext);
	const { data: onboardingStateData } = useGetOnboardingState(
		[
			ONBOARDING_EXPERIENCES.IS_FOUNDING_ADMIN,
			...Object.values(HOME_ONBOARDING_TAKEOVER_V2_ONBOARDING_STATE_KEYS),
		],
		// 0. Feature gate
		!isSiteAdmin || !fg('cc_home_onboarding_takeover_v2_eligibility'),
	);
	const { isFoundingAdmin, takeoverSeenAt } = deserializeState(onboardingStateData);

	// 1. isNewUser: Are they either:
	//      * Someone who has already seen this (i.e. they were previously a new user)?
	//      * OR, are they an eligible new user / NOT an established user?
	const alreadySeenThis = Boolean(takeoverSeenAt);
	const { isEstablishedUser, isEstablishedUserReady, eligibleNewUser } = useOnboardingEligible();
	const isNotEstablishedUser = isEstablishedUserReady && !isEstablishedUser;
	const isNewUser = alreadySeenThis || eligibleNewUser || isNotEstablishedUser;

	// 3. isSiteAndFoundingAdmin: Are they a founding admin?
	const isSiteAndFoundingAdmin = isSiteAdmin && isFoundingAdmin;

	// 4. isLandUser: Are they a land user?
	const { isLandUser } = useUserByLicensedProducts({
		// 0. Feature gate
		skip: !isSiteAdmin || !fg('cc_home_onboarding_takeover_v2_eligibility'),
	});

	// 0. Feature gate
	// Finally return the eligibility if they pass all checks:
	return Boolean(
		isNewUser &&
			isSiteAndFoundingAdmin &&
			isLandUser &&
			fg('cc_home_onboarding_takeover_v2_eligibility'),
	);
};
